import React, { useState, useRef, useEffect } from 'react';
import Slider from 'react-slick';
import { ArrowLeft } from 'lucide-react';
import { PostResponseSchema } from '@api/client';
import PostCard from '@feature/post-item/PostCard';
import { useIsMobile } from '@/hooks/useIsMobile';
import { VideoPlayerModal } from '@feature/play-video';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

interface ShareDrawerProps {
  // Support both single post and multiple posts
  post?: PostResponseSchema;
  posts?: PostResponseSchema[];
  isOpen: boolean;
  onMinimize: () => void;
  onExpand: () => void;
  // Only needed for multiple posts mode
  onPostChange?: (index: number) => void;
  currentPostIndex?: number;
}

export default function ShareDrawer({
  post,
  posts,
  isOpen,
  onMinimize,
  onExpand,
  onPostChange,
  currentPostIndex = 0
}: ShareDrawerProps): React.ReactElement {
  const isMobile = useIsMobile();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<PostResponseSchema | null>(null);
  const isDraggingRef = useRef(false);
  const sliderRef = useRef<any>(null);

  // Determine mode: single post or multiple posts
  const isMultipleMode = posts && posts.length > 0;
  const singlePost = post;

  // Sync carousel position with currentPostIndex when marker is clicked
  useEffect(() => {
    if (sliderRef.current && isMultipleMode) {
      sliderRef.current.slickGoTo(currentPostIndex);
    }
  }, [currentPostIndex, isMultipleMode]);

  // Reset dragging flag after carousel initializes to prevent blocking clicks
  useEffect(() => {
    const timer = setTimeout(() => {
      isDraggingRef.current = false;
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleHandleClick = () => {
    if (isOpen) {
      onMinimize();
    } else {
      onExpand();
    }
  };

  const handleVideoClick = (postData: PostResponseSchema) => {
    if (isDraggingRef.current) {
      return;
    }
    setSelectedPost(postData);
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedPost(null);
  };

  const handleImageClick = (postData: PostResponseSchema) => {
    if (isDraggingRef.current) {
      return;
    }
    setSelectedPost(postData);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedPost(null);
  };

  const handleBeforeChange = () => {
    isDraggingRef.current = true;
  };

  const handleAfterChange = (index: number) => {
    if (onPostChange) {
      onPostChange(index);
    }
    // Reset dragging flag after a brief delay
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 100);
  };

  const carouselSettings = {
    dots: false,
    infinite: false,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: currentPostIndex,
    beforeChange: handleBeforeChange,
    afterChange: (index: number) => handleAfterChange(index),
    arrows: true,
    swipe: true,
    touchMove: true,
    adaptiveHeight: true,
    centerMode: !isMobile,
    centerPadding: isMobile ? '0px' : '60px',
  };

  return (
    <>
      <style>{`
        .layer-posts-carousel {
          background: transparent;
        }
        .layer-posts-carousel .slick-slider,
        .layer-posts-carousel .slick-list,
        .layer-posts-carousel .slick-track {
          background: transparent;
        }
        ${!isMobile ? `
          .layer-posts-carousel .slick-slide {
            transition: opacity 0.3s ease, transform 0.3s ease;
            opacity: 0.5;
            transform: scale(0.85);
          }
          .layer-posts-carousel .slick-slide.slick-center {
            opacity: 1;
            transform: scale(1.05);
          }
        ` : `
          .layer-posts-carousel .slick-slide {
            opacity: 1;
          }
        `}
        .layer-posts-carousel .slick-slide > div {
          padding: ${isMobile ? '0 10px' : '0 5px'};
        }
      `}</style>

      {/* Drawer - always visible, no backdrop */}
      <div className={`post-drawer ${isOpen ? 'open' : 'minimized'}`}>
        {/* Handle bar for visual feedback */}
        <div className="drawer-handle" onClick={handleHandleClick}>
          <div className="handle-bar" />
        </div>

        {/* Drawer content */}
        <div className="drawer-content">
          {/* Post Content */}
          <div className="drawer-body">
            {(() => {
              const postsToRender = isMultipleMode ? posts : singlePost ? [singlePost] : [];

              if (postsToRender.length === 0) {
                return (
                  <div className="no-posts-container">
                    <p>No posts found</p>
                  </div>
                );
              }

              return (
                <div className="layer-posts-carousel">
                  <Slider ref={sliderRef} {...carouselSettings}>
                    {postsToRender.map((postItem) => (
                      <div key={postItem.id} className="carousel-slide">
                        <PostCard
                          post={postItem}
                          onVideoClick={handleVideoClick}
                          onImageClick={handleImageClick}
                        />
                      </div>
                    ))}
                  </Slider>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {isVideoModalOpen && selectedPost?.m3u8_url && (
        <VideoPlayerModal
          isOpen={isVideoModalOpen}
          videoUrl={selectedPost.m3u8_url}
          onClose={closeVideoModal}
          playing
          controls
        />
      )}

      {/* Image Modal */}
      {isImageModalOpen && selectedPost?.photo_url && (
        <div className="image-modal" onClick={closeImageModal}>
          <button className="modal-close-button" onClick={closeImageModal}>
            <ArrowLeft size={28} />
          </button>
          <img
            src={selectedPost.photo_url}
            alt="Post content"
            className="image-modal-content"
          />
        </div>
      )}
    </>
  );
}
